import { useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';

import { SortableHeader } from './SortableHeader';

const DEBOUNCE_MS = 1000;

/**
 * Header con busqueda inline por columna.
 *
 * - Si la columna NO es buscable (`meta.searchable !== true`), renderiza solo el SortableHeader.
 * - Si lo es, agrega un boton lupita; al pulsarlo el header se reemplaza por un input.
 * - Mientras el usuario escribe, despues de `DEBOUNCE_MS` se notifica al padre (`onChange`).
 * - Si tras el debounce el input queda vacio, vuelve al modo header normal.
 *
 * El padre (SmartTable -> StudentsPage) almacena los valores en `query.columnSearch`,
 * lo persiste con `useTablePrefs` (sessionStorage + backend) y los envia al servidor de datos.
 */
export const SearchableHeader = ({ header, value = '', onChange }) => {
	const column = header.column;
	const searchable = column.columnDef.meta?.searchable === true;
	const placeholder = column.columnDef.meta?.searchPlaceholder
		|| column.columnDef.meta?.label
		|| 'Buscar...';

	const inputRef = useRef(null);
	const debounceRef = useRef(null);
	const [isInputMode, setIsInputMode] = useState(Boolean(value));
	const [localValue, setLocalValue] = useState(value);

	// Si el valor externo cambia (reset de filtros, hidratacion), sincronizar.
	useEffect(() => {
		setLocalValue(value);
		if (value) setIsInputMode(true);
	}, [value]);

	useEffect(() => () => {
		if (debounceRef.current) clearTimeout(debounceRef.current);
	}, []);

	if (!searchable) {
		return <SortableHeader header={header} />;
	}

	const commit = (next) => {
		const trimmed = (next || '').trim();
		if (trimmed !== (value || '')) {
			onChange?.(trimmed);
		}
		if (!trimmed) {
			setIsInputMode(false);
		}
	};

	const handleEnterInputMode = (e) => {
		e?.stopPropagation?.();
		setIsInputMode(true);
		setTimeout(() => inputRef.current?.focus(), 0);
	};

	const handleChange = (e) => {
		const next = e.target.value;
		setLocalValue(next);
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => commit(next), DEBOUNCE_MS);
	};

	const handleClear = (e) => {
		e?.stopPropagation?.();
		if (debounceRef.current) clearTimeout(debounceRef.current);
		setLocalValue('');
		commit('');
	};

	const handleBlur = () => {
		// Si quedo vacio sin cambios pendientes, salir del modo input.
		if (!localValue.trim()) {
			if (debounceRef.current) clearTimeout(debounceRef.current);
			commit('');
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Escape') {
			e.preventDefault();
			handleClear();
			inputRef.current?.blur();
		} else if (e.key === 'Enter') {
			e.preventDefault();
			if (debounceRef.current) clearTimeout(debounceRef.current);
			commit(localValue);
		}
	};

	if (!isInputMode) {
		return (
			<div className='relative flex h-7 w-full items-center gap-0.5 pr-1'>
				<div className='min-w-0 flex-1'>
					<SortableHeader header={header} />
				</div>
				<button
					type='button'
					onClick={handleEnterInputMode}
					aria-label={`Buscar en ${placeholder}`}
					className='inline-flex size-5 shrink-0 items-center justify-center rounded text-slate-400 transition-colors hover:bg-indigo-50 hover:text-indigo-600'
				>
					<MagnifyingGlassIcon className='size-3.5' aria-hidden='true' />
				</button>
			</div>
		);
	}

	return (
		<div className='relative flex h-full w-full items-center pr-2'>
			<div className='relative w-full'>
				<MagnifyingGlassIcon
					className='pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400'
					aria-hidden='true'
				/>
				<input
					ref={inputRef}
					type='text'
					value={localValue}
					onChange={handleChange}
					onBlur={handleBlur}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					className='block w-full rounded-md border border-gray-200 bg-white py-1 pl-7 pr-7 text-sm font-normal normal-case tracking-normal text-gray-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500'
				/>
				{localValue && (
					<button
						type='button'
						onMouseDown={(e) => e.preventDefault()}
						onClick={handleClear}
						aria-label='Limpiar busqueda'
						className='absolute right-1.5 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700'
					>
						<XMarkIcon className='h-3.5 w-3.5' aria-hidden='true' />
					</button>
				)}
			</div>
		</div>
	);
};
